import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ProcessingRuleUsageView } from "@/entities/all";
import { Code, CheckCircle, Sparkles, ArrowDownCircle, ArrowUpCircle, PlusCircle } from "lucide-react";

import ProcessingRulesTable from "../components/processing-rules/ProcessingRulesTable";
import ProcessingRuleFilters from "../components/processing-rules/ProcessingRuleFilters";
import CreateProcessingRuleModal from "../components/processing-rules/CreateProcessingRuleModal";
import DeleteProcessingRuleDialog from "../components/processing-rules/DeleteProcessingRuleDialog";
import ProcessingRuleDetailsSheet from "../components/processing-rules/ProcessingRuleDetailsSheet";
import { ProcessingRulesImportDialog } from "../components/processing-rules-import/ProcessingRulesImportDialog";
import { exportProcessingRules } from "../utils/excelTemplateGenerator";
import GuidedChooser, { startGuided } from "../components/guided/GuidedChooser";
import PaginatedTable from "../components/ui/paginated-table";
import { toast } from "sonner";

export default function ProcessingRules() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rules, setRules] = useState([]);
  const [filteredRules, setFilteredRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ rule_type: 'all', rule_language: 'all', status: 'all' });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isChooserOpen, setIsChooserOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [selectedRuleId, setSelectedRuleId] = useState(searchParams.get('id') ? Number(searchParams.get('id')) : null);
  const [isSheetOpen, setIsSheetOpen] = useState(!!searchParams.get('id'));

  useEffect(() => { loadProcessingRules(); }, []);
  useEffect(() => { filterRules(); }, [rules, searchTerm, filters]);

  const loadProcessingRules = async () => {
    setIsLoading(true);
    try {
      const data = await ProcessingRuleUsageView.list('rule_name');
      setRules(data);
    } catch (error) {
      console.error('Error loading processing rules:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterRules = () => {
    let filtered = rules;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(rule => rule.rule_name?.toLowerCase().includes(search));
    }
    if (filters.rule_type !== 'all') filtered = filtered.filter(r => r.rule_type === filters.rule_type);
    if (filters.rule_language !== 'all') filtered = filtered.filter(r => r.rule_language === filters.rule_language);
    if (filters.status !== 'all') filtered = filtered.filter(r => String(r.status || '').toLowerCase() === String(filters.status).toLowerCase());
    setFilteredRules(filtered);
  };

  const handleRuleCreated = () => { loadProcessingRules(); setIsCreateModalOpen(false); };
  const handleRuleDeleted = () => { loadProcessingRules(); setIsDeleteDialogOpen(false); setSelectedRule(null); };

  const handleRowClick = (rule) => {
    setSelectedRuleId(rule.id);
    setIsSheetOpen(true);
    const params = { id: rule.id };
    const tab = searchParams.get('tab');
    if (tab) params.tab = tab;
    setSearchParams(params);
  };

  const handleSheetClose = () => { setIsSheetOpen(false); setSelectedRuleId(null); setSearchParams({}); };
  const handleSheetSaved = () => { loadProcessingRules(); };
  const handleDeleteRule = (rule) => { setSelectedRule(rule); setIsDeleteDialogOpen(true); };
  const handleImportComplete = () => { loadProcessingRules(); setIsImportDialogOpen(false); };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      exportProcessingRules(rules);
      toast.success('Processing rules exported successfully');
    } catch (error) {
      console.error('Error exporting rules:', error);
      toast.error('Failed to export processing rules');
    } finally {
      setIsExporting(false);
    }
  };

  const stats = {
    total: rules.length,
    // Status is compared case-insensitively: the MCP tool and the fleet write
    // 'active' where the UI's default is 'Active', and Corvane's 22 active rules
    // showed as 'Active 0' (2026-09-18).
    active: rules.filter(r => String(r.status || '').toLowerCase() === 'active').length,
    totalUsage: rules.reduce((sum, r) => sum + (parseInt(r.usage_count, 10) || 0), 0),
  };

  return (
    <div data-tour="page-processing-rules" className="pt-5 pb-24 px-4 md:py-[30px] md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Title + buttons */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">Processing Rules</h1>
          <p className="text-sm font-normal text-page-muted">Reusable data validation and transformation rules</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleExport}
            disabled={rules.length === 0 || isExporting}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active text-sm font-medium cursor-pointer bg-card-bg disabled:opacity-50 disabled:cursor-not-allowed hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <ArrowDownCircle size={24} />
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
          <button
            onClick={() => setIsImportDialogOpen(true)}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active text-sm font-medium cursor-pointer bg-card-bg hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <ArrowUpCircle size={24} />
            Import
          </button>
          <button
            onClick={() => setIsChooserOpen(true)}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 hover:bg-nav-active-dark transition-colors"
          >
            <PlusCircle size={24} />
            New Rule
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
        <div className="bg-card-bg rounded-[5px] h-[83px] px-5 py-3 flex items-start gap-3">
          <Code size={24} color="#377AD6" className="shrink-0" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-page-text">Total Rules</span>
            <span className="text-[30px] font-medium leading-none text-page-text">{stats.total}</span>
          </div>
        </div>
        <div className="bg-card-bg rounded-[5px] h-[83px] px-5 py-3 flex items-start gap-3">
          <CheckCircle size={24} color="rgba(77,179,0,0.9)" className="shrink-0" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-page-text">Active</span>
            <span className="text-[30px] font-medium leading-none text-page-text">{stats.active}</span>
          </div>
        </div>
        <div className="bg-card-bg rounded-[5px] h-[83px] px-5 py-3 flex items-start gap-3">
          <Sparkles size={24} color="rgba(233,171,48,0.9)" className="shrink-0" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-page-text">Total Usage</span>
            <span className="text-[30px] font-medium leading-none text-page-text">{stats.totalUsage}</span>
          </div>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] flex items-center h-9 bg-card-bg rounded-[18px] px-3 gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#377AD6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            placeholder="Search processing rules..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-transparent text-sm outline-none border-0 text-page-text"
          />
        </div>
        <ProcessingRuleFilters filters={filters} onFiltersChange={setFilters} />
      </div>

      {/* Table */}
      <PaginatedTable
        data={filteredRules}
        externalLoading={isLoading}
        renderTable={(paginatedData, loading) => (
          <div className="bg-card-bg rounded-[8px] overflow-x-auto">
            <ProcessingRulesTable
              rules={paginatedData}
              isLoading={loading}
              onDeleteRule={handleDeleteRule}
              onRowClick={handleRowClick}
            />
          </div>
        )}
      />

      {isChooserOpen && (
        <GuidedChooser
          title="New processing rule"
          manualLabel="In the form"
          // The form now opens on its own in-panel assistant (describe it →
          // review the draft → Create), with the hand-written path one click
          // away. So "Manual" was no longer true: this route is not
          // assistant-less, it is the SELF-CONTAINED one. The guided route
          // still means "take me to the chat", which also attaches the rule to
          // an element — the thing the form does not do.
          manualHint="Describe the rule and review the draft, or write the code yourself — without leaving the page."
          guidedHint="Open the chat assistant, which can also attach the new rule to a data element."
          onManual={() => { setIsChooserOpen(false); setIsCreateModalOpen(true); }}
          onGuided={() => { setIsChooserOpen(false); startGuided("processing_rule"); }}
          onClose={() => setIsChooserOpen(false)}
        />
      )}

      <CreateProcessingRuleModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onRuleCreated={handleRuleCreated}
      />

      {selectedRule && (
        <DeleteProcessingRuleDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => { setIsDeleteDialogOpen(false); setSelectedRule(null); }}
          onRuleDeleted={handleRuleDeleted}
          rule={selectedRule}
        />
      )}

      <ProcessingRulesImportDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onImportComplete={handleImportComplete}
      />

      <ProcessingRuleDetailsSheet
        ruleId={selectedRuleId}
        isOpen={isSheetOpen}
        onClose={handleSheetClose}
        onSaved={handleSheetSaved}
        defaultTab={searchParams.get('tab') || 'details'}
        onTabChange={(tab) => setSearchParams({ id: selectedRuleId, tab })}
      />
    </div>
  );
}
